import { HttpError } from '../../../api';
import { SSE_START, parseSseChunk } from '../stream/sse';
import type { SseEvent } from '../stream/sse';
import type { NotificationSignal } from '../types/domain.types';

import { parseSignal } from './schemas';

const EVENT_STREAM = 'text/event-stream';
const READY_EVENT = 'ready';
const SIGNAL_EVENT = 'signal';
const RETRY_AFTER = 'Retry-After';
const WHOLE_SECONDS = /^\d+$/;

export class SignalStreamError extends HttpError {
  readonly retryAfterMs: number | null;

  constructor(status: number, retryAfterMs: number | null = null) {
    super({ status, message: `The live signal stream answered ${status}` });
    this.name = 'SignalStreamError';
    this.retryAfterMs = retryAfterMs;
  }
}

export const retryAfterMsOf = (value: string | null, now: number): number | null => {
  const text = value?.trim() ?? '';
  if (WHOLE_SECONDS.test(text)) return Number(text) * 1_000;
  const at = Date.parse(text);
  return Number.isNaN(at) ? null : Math.max(at - now, 0);
};

export interface SignalStreamHandlers {
  readonly signal: AbortSignal;
  readonly onReady: () => void;
  readonly onSignal: (signal: NotificationSignal) => void;
  readonly onActivity?: () => void;
}

const deliver = (event: SseEvent, { onReady, onSignal }: SignalStreamHandlers): void => {
  if (event.type === READY_EVENT) {
    onReady();
    return;
  }
  if (event.type !== SIGNAL_EVENT) return;
  const signal = parseSignal(event.data);
  if (signal !== null) onSignal(signal);
};

const readEvents = async (body: ReadableStream<Uint8Array>, handlers: SignalStreamHandlers): Promise<void> => {
  const reader = body.getReader();
  const release = (): void => {
    reader.cancel().catch(() => undefined);
  };
  handlers.signal.addEventListener('abort', release, { once: true });
  const decoder = new TextDecoder();
  let state = SSE_START;
  try {
    for (;;) {
      if (handlers.signal.aborted) return;
      const chunk = await reader.read();
      if (chunk.done || handlers.signal.aborted) return;
      handlers.onActivity?.();
      const step = parseSseChunk(state, decoder.decode(chunk.value, { stream: true }));
      state = step.state;
      step.events.forEach((event) => deliver(event, handlers));
    }
  } finally {
    handlers.signal.removeEventListener('abort', release);
    release();
  }
};

export const readSignalStream = async (url: string, handlers: SignalStreamHandlers): Promise<void> => {
  try {
    const response = await fetch(url, {
      headers: { Accept: EVENT_STREAM },
      cache: 'no-store',
      credentials: 'include',
      signal: handlers.signal,
    });
    if (!response.ok) throw new SignalStreamError(response.status, retryAfterMsOf(response.headers.get(RETRY_AFTER), Date.now()));
    if (response.body !== null) await readEvents(response.body, handlers);
  } catch (error) {
    if (!handlers.signal.aborted) throw error;
  }
};
